import { useFormik } from "formik";
import { useEffect } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { ExpenseCategory } from "components/common/CategorySearchAutoComplete";
import { AllocationIdAndTypeProps } from "components/common/AllocationSearchAutoComplete";
import useCustomFieldRenderer from "hooks/useCustomFieldRenderer";
import { CustomFieldsRecord } from "hooks/customFields.types";

export interface FormikTransactionRequirements {
  attendeeDetails: any;
  expenseCategory: ExpenseCategory;
  allocationCode: {
    id: string;
    allocationCode: string;
  };
  allocationType: {
    key: string;
    name: string;
  };
  memo: string;
  customFields?: CustomFieldsRecord;
}

interface useTransactionRequirementsProps {
  transaction: any;
  transactionId: string;
  allocationData?: any;
  updateTransaction: (options?: any) => Promise<any>;
  handleRefreshTransactions: (() => void) | undefined;
}

export function useTransactionRequirements({
  transaction,
  transactionId,
  updateTransaction,
  handleRefreshTransactions,
}: useTransactionRequirementsProps) {
  const { t } = useTranslation();
  const { transformCustomFieldsToArray, transformCustomFieldsToRecord } =
    useCustomFieldRenderer({});

  const formik = useFormik<FormikTransactionRequirements>({
    initialValues: {
      attendeeDetails: {
        attendeeGroupName: "",
        totalAttendees: "",
        notes: "",
      },
      expenseCategory: {
        id: "",
        name: "",
        level: 0,
      },
      allocationCode: {
        id: "",
        allocationCode: "",
      },
      allocationType: {
        key: "",
        name: "",
      },
      memo: "",
      customFields: {},
    },
    onSubmit: (values) => {
      let input = {};

      if (values.attendeeDetails && values.attendeeDetails?.totalAttendees) {
        input = {
          attendeeDetails: {
            ...values.attendeeDetails,
            totalAttendees: parseInt(values.attendeeDetails?.totalAttendees),
          },
        };
      }

      if (values.expenseCategory.id) {
        input = {
          ...input,
          expenseCategoryId: values.expenseCategory.id,
        };
      }

      if (values.allocationType?.key && !values.allocationCode?.id) {
        toast.error(t("Please select allocation name"));
        return;
      }

      if (values.allocationType?.key && values.allocationCode?.id) {
        input = {
          ...input,
          allocationCodeId: values.allocationCode.id,
        };
      }

      if (!values.allocationType?.name) {
        input = {
          ...input,
          allocationCodeId: null,
        };
      }

      if (values.memo) {
        input = {
          ...input,
          memo: values.memo,
        };
      }

      if (values.customFields) {
        input = {
          ...input,
          customFields: transformCustomFieldsToArray(values.customFields),
        };
      }

      updateTransaction({
        variables: {
          id: transactionId,
          input: {
            ...input,
          },
        },
      })
        .then(() => {
          formik.resetForm({ values });
          handleRefreshTransactions && handleRefreshTransactions();
        })
        .catch((error) => {
          toast.error(t("Transaction failed to update"));
        });
    },
  });

  useEffect(() => {
    if (transaction?.id) {
      const initialValues = {
        attendeeDetails: {
          attendeeGroupName:
            transaction.attendeeDetails?.attendeeGroupName || "",
          totalAttendees: transaction.attendeeDetails?.totalAttendees || 0,
          notes: transaction.attendeeDetails?.notes || "",
        },
        expenseCategory: transaction.expenseCategory || "",
        allocationCode: {
          id: transaction.allocationCode?.id || "",
          allocationCode: transaction.allocationCode?.allocationCode || "",
        },
        allocationType: {
          key: transaction.allocationType?.key || "",
          name: transaction.allocationType?.name || "",
        },
        memo: transaction.memo || "",
        customFields: transformCustomFieldsToRecord(
          transaction?.customFields || [],
        ),
      };
      formik.resetForm({ values: initialValues });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transaction?.id]);

  const handleAllocationSelect = (
    allocationIdAndType: AllocationIdAndTypeProps,
  ) => {
    formik.setFieldValue(
      "allocationType.key",
      allocationIdAndType.allocationType.key,
    );
    formik.setFieldValue(
      "allocationType.name",
      allocationIdAndType.allocationType.name,
    );
    formik.setFieldValue("allocationCode.id", allocationIdAndType.allocationId);
    formik.setFieldValue(
      "allocationCode.allocationCode",
      allocationIdAndType.allocationCode,
    );
  };

  return { formik, handleAllocationSelect };
}
